import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { GlowButton } from "@/components/GlowButton";
import { GlassCard } from "@/components/GlassCard";
import { SectionHeader } from "@/components/SectionHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useSeo } from "@/hooks/useSeo";
import { SOLUTIONS } from "@/lib/data";

const container = "mx-auto max-w-6xl px-4 sm:px-6 lg:px-8";
const VP_20 = { once: true, amount: 0.2 };

function SolutionCard({ p, i, large }) {
  const [t, setT] = React.useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ x: px * 5, y: -py * 5 });
  };
  const reset = () => setT({ x: 0, y: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VP_20}
      transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
    >
      <Dialog>
        <DialogTrigger asChild>
          <button
            className="block w-full text-left"
            onMouseMove={onMove}
            onMouseLeave={reset}
            data-testid={`solutions-card-${p.id}`}
          >
            <GlassCard
              glow={i % 2 ? "purple" : "green"}
              className="h-full overflow-hidden"
              style={{ transform: `perspective(900px) rotateY(${t.x}deg) rotateX(${t.y}deg)` }}
            >
              <div className={`relative overflow-hidden ${large ? "h-56" : "h-44"}`}>
                <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover opacity-80 transition-transform duration-700 hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,11,13,0.92)] via-transparent to-transparent" />
                <span className="absolute left-3 top-3 rounded-full glass px-2.5 py-1 text-[10px] uppercase tracking-wider text-neon-g">{p.category}</span>
                <span className="absolute right-3 top-3 rounded-full glass px-2.5 py-1 text-[10px] uppercase tracking-wider text-muted-brand">Case study</span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-semibold text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-brand">{p.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.stack.slice(0, 3).map((s) => (
                    <span key={s} className="rounded-full border border-[rgba(255,255,255,0.1)] px-2.5 py-1 text-[11px] text-muted-brand">{s}</span>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-4">
                  {p.results.map((r) => (
                    <span key={r} className="text-xs font-semibold text-neon-g">{r}</span>
                  ))}
                </div>
              </div>
            </GlassCard>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl border-[rgba(255,255,255,0.1)] bg-[color:var(--bg-1)] text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{p.title}</DialogTitle>
            <DialogDescription className="text-muted-brand">{p.category} · case study</DialogDescription>
          </DialogHeader>
          <div className="relative h-44 overflow-hidden rounded-xl">
            <img src={p.image} alt={p.title} className="h-full w-full object-cover opacity-85" />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,11,13,0.85)] to-transparent" />
            <span className="absolute left-3 top-3 rounded-full glass px-2.5 py-1 text-[10px] uppercase tracking-wider text-neon-g">{p.category}</span>
          </div>
          <div className="space-y-4 text-sm leading-relaxed">
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-neon-g">Challenge</div>
              <p className="mt-1 text-muted-brand">{p.challenge}</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-neon-g">Solution</div>
              <p className="mt-1 text-muted-brand">{p.solution}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {p.stack.map((s) => (
                <span key={s} className="rounded-full border border-[rgba(255,255,255,0.1)] px-2.5 py-1 text-[11px] text-muted-brand">{s}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 border-t border-[rgba(255,255,255,0.08)] pt-4">
              {p.results.map((r) => (
                <span key={r} className="text-sm font-semibold text-neon-g">{r}</span>
              ))}
            </div>
          </div>
          <div className="mt-2">
            <GlowButton as={Link} to="/contact" variant="secondary" className="w-full" data-testid={`solutions-cta-${p.id}`}>
              Discuss a similar project <ExternalLink size={15} />
            </GlowButton>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

export default function Solutions() {
  useSeo({
    title: "Solutions — Métis Tech Labs",
    description:
      "Case studies representing AI automation, CRM development, enterprise dashboards, AI agents, custom websites, and workflow integrations by Métis Tech Labs.",
  });

  const featured = SOLUTIONS.filter((s) => s.featured);
  const caseStudies = SOLUTIONS.filter((s) => !s.featured);

  return (
    <div className="pt-32">
      <section className={container}>
        <SectionHeader
          eyebrow="Solutions"
          title="Intelligent systems, real outcomes"
          subtitle="Illustrative case studies showcasing the solutions we build — structured so real client projects can replace them."
        />
      </section>

      {/* FEATURED PROJECTS */}
      <section className={`${container} mt-16`}>
        <div className="mb-8 flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--neon-g)] shadow-[0_0_10px_var(--neon-g)]" />
          <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">Featured Projects</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {featured.map((p, i) => (
            <SolutionCard key={p.id} p={p} i={i} large />
          ))}
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className={`${container} mt-20`}>
        <div className="mb-8 flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--neon-p)] shadow-[0_0_10px_var(--neon-p)]" />
          <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">Case Studies</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {caseStudies.map((p, i) => (
            <SolutionCard key={p.id} p={p} i={i} />
          ))}
        </div>
      </section>

      <section className={`${container} mt-20`}>
        <div className="neon-frame relative overflow-hidden rounded-3xl glass-strong px-6 py-12 text-center sm:px-12">
          <h2 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            Your project could be next
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-muted-brand">
            Let’s design a system that delivers measurable outcomes for your business.
          </p>
          <div className="mt-7 flex justify-center">
            <GlowButton as={Link} to="/contact" pulse data-testid="solutions-cta-button">
              Start a project <ArrowUpRight size={16} />
            </GlowButton>
          </div>
        </div>
      </section>
    </div>
  );
}
